import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  CalendarPlus,
  CheckCheck,
  Clock,
  Fingerprint,
  Receipt,
  UserRound,
} from 'lucide-react';
import { HomeCard } from './HomeCard.js';
import { type ModuleKey, type Permission } from '@hrms/shared';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { homeAccess } from './homeAccess.js';

interface QuickAction {
  label: string;
  to: string;
  icon: ReactNode;
  /** Hidden when the caller lacks it — same rule the nav applies, so Home never offers a dead end. */
  permission?: Permission;
  /** Hidden while the company has this module switched off, whoever the person is. */
  module?: ModuleKey;
}

/**
 * Only routes that exist today. When the payslip, help desk or documents pages ship, add them
 * here — a tile that lands on "not found" is worse than no tile.
 */
const ACTIONS: QuickAction[] = [
  {
    label: 'Apply Leave',
    to: '/leave?new=1',
    icon: <CalendarPlus className="size-5" />,
    permission: 'leave:request',
    module: 'leave',
  },
  {
    label: 'Submit Reimbursement',
    to: '/claims',
    icon: <Receipt className="size-5" />,
    permission: 'expense:submit',
    module: 'expense_reimbursement',
  },
  {
    label: 'My Attendance',
    to: '/attendance/my',
    icon: <Fingerprint className="size-5" />,
    module: 'attendance',
  },
  {
    label: 'Regularise Attendance',
    to: '/attendance/requests',
    icon: <Clock className="size-5" />,
    module: 'attendance',
  },
  { label: 'Update Profile', to: '/my-details', icon: <UserRound className="size-5" /> },
  {
    label: 'Holiday Calendar',
    to: '/leave/holidays',
    icon: <CalendarDays className="size-5" />,
    module: 'leave',
  },
  {
    label: 'Approve Leaves',
    to: '/inbox?module=leave',
    icon: <CheckCheck className="size-5" />,
    permission: 'leave:approve',
    module: 'leave',
  },
];

export function QuickActions() {
  const { actor, me } = useAuthenticatedSession();
  const visible = ACTIONS.filter((a) => homeAccess(actor, me.enabledModules, a));

  return (
    <HomeCard title="Quick Actions">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {visible.map((action) => (
          <Link
            key={action.label}
            to={action.to}
            className="flex flex-col items-center justify-center gap-2 rounded-xl border border-border bg-bg-subtle p-3 text-center transition-all hover:bg-bg-subtle hover:border-border-strong"
          >
            <span className="text-accent">{action.icon}</span>
            <span className="text-[11px] font-semibold text-fg leading-tight">{action.label}</span>
          </Link>
        ))}
      </div>
    </HomeCard>
  );
}
